// Arm configuration for the Graduation Signaling A/B test (Mandel & Fishbach ×
// Rise Science). Two web arms are built here — "signaling" and "control" — both
// duplicated from the I03 name-greeting paywall. The "best practice / current"
// arm in the copy deck is the existing production paywall and is not rebuilt.
//
// Only copy differs between the arms: the header above the plan grid, the CTA
// label, and (control only) the plan-card labels. See the copy deck, Screen 5.

import {PaywallSlug} from '../../analytics/paywallEvents';

export type GraduationArm = 'signaling' | 'control';

export type ArmConfig = {
  arm: GraduationArm;
  /** Registered `paywall` slug for the shared "[Web Payments] Paywall *" events (one per arm). */
  paywallSlug: PaywallSlug;
  /** Post-purchase trial-timeline route for this arm. */
  trialRoute: string;
  /** Big header above the plan grid. `isWeek` = the free-week option is selected. */
  bigHeader: (isWeek: boolean) => string;
  /** Small header line under the big header. Personalized with `firstName` when available. */
  smallHeader: (isWeek: boolean, firstName?: string | null) => string;
  /** CTA label. */
  cta: (isWeek: boolean) => string;
  /**
   * Control relabels the plan cards ("Try free · 7-Day Trial", "$0.99 · 30-Day
   * Trial"); signaling keeps the current best-practice labels.
   */
  relabelCards: boolean;
};

// Personalized subheader: prefixes the user's first name when available
// ("Alex, you keep the benefits"), otherwise capitalizes the phrase so it reads
// as a standalone line ("You keep the benefits").
function personalizeSubheader(phrase: string, firstName?: string | null): string {
  if (firstName) return `${firstName}, ${phrase}`;
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

// NOTE: the copy deck only specifies the *week* wording for the big header. The
// month wording ("… a month with RISE") is inferred to parallel the week copy.
export const GRADUATION_ARMS: Record<GraduationArm, ArmConfig> = {
  signaling: {
    arm: 'signaling',
    paywallSlug: 'graduation-signaling',
    trialRoute: '/graduation-signaling-trial',
    bigHeader: isWeek => (isWeek ? 'Finish a week with RISE' : 'Finish a month with RISE'),
    smallHeader: (isWeek, firstName) =>
      personalizeSubheader(isWeek ? 'you keep the benefits' : 'keep the (extra) benefits', firstName),
    cta: isWeek => (isWeek ? 'COMPLETE FREE WEEK' : 'COMPLETE TRIAL MONTH'),
    relabelCards: false,
  },
  control: {
    arm: 'control',
    paywallSlug: 'graduation-control',
    trialRoute: '/graduation-control-trial',
    bigHeader: isWeek => (isWeek ? 'Try a week with RISE' : 'Try a month with RISE'),
    smallHeader: (isWeek, firstName) =>
      personalizeSubheader(isWeek ? 'experience the benefits' : 'experience the (extra) benefits', firstName),
    cta: isWeek => (isWeek ? 'TRY FOR A WEEK' : 'TRY FOR A MONTH'),
    relabelCards: true,
  },
};
